import type { Dispatch } from '@reduxjs/toolkit';

import type { SendMessageResponse } from '../sse/publishStreamingMessage';
import { SidekickHttpError } from '../sse/sidekickHttp';
import { parseWireArtifacts } from '../sse/wireArtifacts';
import {
  addMessage,
  setCurrentTraceId,
  setIsStreaming,
} from '../state/aiSidekickChatSlice';
import type {
  ArtifactRef,
  Citation,
  Message,
  VisualizationImageRef,
} from '../state/chatTypes';
import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import { isValidMessageRole } from '../state/messageRole';
import { parseVisualizationData } from '../structuredQuery/parse-visualization-data';
import type { VisualizationData } from '../structuredQuery/types';

/**
 * Wire shape of `Message` returned by the BE. Mirrors the
 * `envolve.sidekick.v1.Message` proto serialized via `betterproto`'s
 * camelCase JSON convention. Optional / unset proto fields surface as
 * empty strings, empty maps, or empty arrays — the mappers below
 * strip those back to `undefined` for the FE `Message` shape.
 */
export type WireMessage = {
  id: string;
  conversationId: string;
  role: string;
  content: string;
  name?: string;
  createdAt: string;
  metadata?: Record<string, string>;
  visualizationUrls?: ReadonlyArray<string>;
  citations?: ReadonlyArray<Citation>;
  artifacts?: ReadonlyArray<ArtifactRef>;
  parentId?: string;
};

const mapWireVisualizationUrls = (
  urls: ReadonlyArray<string> | undefined
): ReadonlyArray<VisualizationImageRef> | undefined => {
  if (urls === undefined || urls.length === 0) {
    return undefined;
  }
  return urls.map((url, index) => ({ url, position: index }));
};

const buildMalformedAssistantMessageError = () =>
  new SidekickHttpError(0, {
    code: SYNTHETIC_TRANSPORT_ERROR_CODE,
    message: 'Sidekick sync: assistantMessage payload is malformed',
  });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringRecord = (value: unknown): value is Record<string, string> =>
  isRecord(value) &&
  Object.values(value).every((entry) => typeof entry === 'string');

const stringFromRecord = (
  raw: Record<string, unknown>,
  key: string
): string => {
  const value = raw[key];
  return typeof value === 'string' ? value : '';
};

const parseWireCitations = (
  raw: unknown
): ReadonlyArray<Citation> | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const citations = raw.reduce<Citation[]>((acc, entry) => {
    if (!isRecord(entry)) {
      return acc;
    }
    const index = entry.index;
    const displayLabel = stringFromRecord(entry, 'displayLabel').trim();
    if (
      typeof index !== 'number' ||
      !Number.isInteger(index) ||
      index <= 0 ||
      displayLabel === ''
    ) {
      return acc;
    }
    const url = stringFromRecord(entry, 'url');
    acc.push({ index, displayLabel, ...(url !== '' ? { url } : {}) });
    return acc;
  }, []);
  return citations.length > 0 ? citations : undefined;
};

const parseWireMessage = (value: unknown): WireMessage => {
  if (!isRecord(value)) {
    throw buildMalformedAssistantMessageError();
  }

  const id = typeof value.id === 'string' ? value.id : '';
  const conversationId =
    typeof value.conversationId === 'string' ? value.conversationId : '';
  const createdAt = typeof value.createdAt === 'string' ? value.createdAt : '';
  if (id === '' || conversationId === '' || createdAt === '') {
    throw buildMalformedAssistantMessageError();
  }

  const metadata = isStringRecord(value.metadata) ? value.metadata : undefined;
  const visualizationUrls = Array.isArray(value.visualizationUrls)
    ? value.visualizationUrls.filter(
        (url): url is string => typeof url === 'string'
      )
    : undefined;
  const citations = parseWireCitations(value.citations);
  const artifacts = parseWireArtifacts(value.artifacts);

  return {
    id,
    conversationId,
    role: typeof value.role === 'string' ? value.role : '',
    content: typeof value.content === 'string' ? value.content : '',
    ...(typeof value.name === 'string' ? { name: value.name } : {}),
    createdAt,
    ...(metadata !== undefined ? { metadata } : {}),
    ...(visualizationUrls !== undefined ? { visualizationUrls } : {}),
    ...(citations !== undefined ? { citations } : {}),
    ...(artifacts !== undefined ? { artifacts } : {}),
    ...(typeof value.parentId === 'string' && value.parentId !== ''
      ? { parentId: value.parentId }
      : {}),
  };
};

/**
 * Translates the wire `assistantMessage` payload into the FE
 * `Message` shape. Strips betterproto empty defaults (`name: ''`,
 * empty metadata maps, empty visualization arrays) so consumers can
 * use truthy / `=== undefined` checks instead of guarding against
 * empty strings everywhere. Falls back to `'assistant'` when the BE
 * supplies a role value the FE union doesn't recognize — defensive
 * since the proto leaves `role` open-ended.
 */
export const mapWireMessageToFe = (
  wire: WireMessage,
  traceId?: string,
  visualization?: VisualizationData
): Message => {
  const visualizationUrls = mapWireVisualizationUrls(wire.visualizationUrls);
  const metadata =
    wire.metadata !== undefined && Object.keys(wire.metadata).length > 0
      ? wire.metadata
      : undefined;
  const responseTraceId =
    traceId !== undefined && traceId !== '' ? traceId : undefined;
  return {
    id: wire.id,
    conversationId: wire.conversationId,
    role: isValidMessageRole(wire.role) ? wire.role : 'assistant',
    content: wire.content,
    ...(wire.name !== undefined && wire.name !== '' ? { name: wire.name } : {}),
    createdAt: wire.createdAt,
    ...(metadata !== undefined ? { metadata } : {}),
    ...(responseTraceId !== undefined ? { traceId: responseTraceId } : {}),
    ...(visualization !== undefined ? { visualization } : {}),
    ...(visualizationUrls !== undefined ? { visualizationUrls } : {}),
    ...(wire.citations !== undefined ? { citations: wire.citations } : {}),
    ...(wire.artifacts !== undefined ? { artifacts: wire.artifacts } : {}),
    ...(wire.parentId !== undefined && wire.parentId !== ''
      ? { parentId: wire.parentId }
      : {}),
  };
};

const parseResponseVisualization = (
  response: SendMessageResponse
): VisualizationData | undefined => {
  const visualization = response.visualization?.visualization;
  if (visualization === undefined) {
    return undefined;
  }
  return parseVisualizationData(visualization) ?? undefined;
};

/**
 * Dispatches slice updates for a sync-mode `SendMessageResponse`
 * (`Accept: application/json` with `assistantMessage` populated).
 * Clears streaming lifecycle flags on every exit path that reaches
 * a successful parse.
 */
export const applySendMessageResponseToSlice = (
  dispatch: Dispatch,
  response: SendMessageResponse
): void => {
  if (response.assistantMessage !== undefined) {
    const visualization = parseResponseVisualization(response);
    dispatch(
      addMessage(
        mapWireMessageToFe(
          parseWireMessage(response.assistantMessage),
          response.traceId,
          visualization
        )
      )
    );
  }
  if (response.traceId !== undefined && response.traceId !== '') {
    dispatch(setCurrentTraceId(response.traceId));
  }
  dispatch(setIsStreaming(false));
};
